import React from "react";
import { Link } from "react-router-dom";
import LandingLayout from "../components/LandingLayout";
import { useRedirectIfLoggedIn } from "../hooks/useRedirectIfLoggedIn";
import LandingHero from "../components/landing/LandingHero";
import ProblemsSection from "../components/landing/ProblemsSection";
import SolutionGrid from "../components/landing/SolutionGrid";
import HowItWorks from "../components/landing/HowItWorks";
import EarlyAccessCTA from "../components/landing/EarlyAccessCTA";
import FinalCTASection from "../components/landing/FinalCTASection";

const problems = [
    {
        title: "No sabes el coste real",
        description: "El precio del piso es solo el principio. Sin saber cuánto costará la reforma, no puedes saber si realmente te lo puedes permitir.",
    },
    {
        title: "Presupuestos que tardan semanas",
        description: "Para saber lo que cuesta, necesitas que un constructor visite la vivienda, mida, calcule y te pase un presupuesto. Semanas de espera para cada piso.",
    },
    {
        title: "Negociar a ciegas",
        description: "Sin datos objetivos de reforma, negocias el precio por intuición. El vendedor tiene ventaja porque tú no puedes argumentar con cifras.",
    },
];

const steps = [
    {
        title: "Introduce la vivienda",
        description: "Pega la URL de Idealista o Fotocasa, o introduce los datos manualmente.",
    },
    {
        title: "La IA analiza todo",
        description: "Fotos, texto del anuncio, datos de mercado de la zona. En segundos tienes el resultado.",
    },
    {
        title: "Descarga tu informe",
        description: "PDF con coste de reforma desglosado y presupuesto total. Listo para negociar o decidir.",
    },
];

const features = [
    {
        icon: "💰",
        title: "Coste de reforma desglosado",
        description: "Por partidas: albañilería, fontanería, electricidad, acabados, pintura. Precios de mercado de tu zona.",
    },
    {
        icon: "📈",
        title: "Presupuesto total real",
        description: "Precio de compra + reforma + gastos. Sabrás exactamente cuánto necesitas antes de comprometerte.",
    },
    {
        icon: "🎯",
        title: "Comparativa de escenarios",
        description: "Reforma básica vs. reforma completa: qué incluye cada una, cuánto cuesta y cómo queda tu vivienda.",
    },
    {
        icon: "📄",
        title: "Informe para negociar",
        description: "PDF profesional que puedes presentar al vendedor, al banco o a tu pareja. Datos concretos, no opiniones.",
    },
];

const LandingComprador = () => {
    useRedirectIfLoggedIn("/analizarpropiedad/nueva");
    return (
        <LandingLayout>
            <div className="max-w-5xl mx-auto px-6 pt-4">
                <Link to="/" className="text-sm text-gray-500 hover:text-brand-blue transition-colors">
                    ← Todos los perfiles
                </Link>
            </div>

            <LandingHero
                badge="Para compradores"
                title="Descubre cuánto te costará reformar"
                highlight="antes de decidirte"
                subtitle="Analiza cualquier vivienda que te interese: coste de reforma estimado, presupuesto total real y un informe claro para negociar con datos, no con dudas."
                ctaText="Analizar una vivienda"
                bgClass="bg-gradient-to-br from-purple-50 to-purple-100"
                highlightClass="text-purple-700"
            />

            <ProblemsSection
                title="Comprar para reformar no debería ser un salto de fe"
                subtitle="La vivienda es la decisión financiera más importante de tu vida. Mereces datos antes de firmar."
                problems={problems}
            />

            <HowItWorks steps={steps} />

            <SolutionGrid
                title="Lo que incluye cada análisis"
                solutions={features}
            />

            <section className="py-20 bg-gray-50">
                <div className="max-w-5xl mx-auto px-6">
                    <h2 className="text-3xl font-bold text-brand-blue mb-4 text-center">Sea cual sea tu situación</h2>
                    <p className="text-center text-gray-500 text-lg max-w-2xl mx-auto mb-12">
                        Si estás buscando casa y consideras una que necesita reforma, Reformista te da la claridad que necesitas.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {[
                            {
                                icon: "🏠",
                                title: "Primera vivienda",
                                description: "El presupuesto es ajustado y no puedes permitirte sorpresas. Saber el coste real de la reforma antes de firmar te da tranquilidad y poder de negociación.",
                            },
                            {
                                icon: "🔨",
                                title: "Comprar para reformar a tu gusto",
                                description: "Has visto un piso con potencial pero necesitas saber si el precio + reforma cabe en tu presupuesto. Reformista te da el coste total para que decidas con confianza.",
                            },
                        ].map((item, i) => (
                            <div key={i} className="bg-white shadow rounded-lg p-6 border border-gray-200">
                                <div className="text-3xl mb-3">{item.icon}</div>
                                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                                <p className="text-gray-500 text-sm">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <EarlyAccessCTA />

            <FinalCTASection
                title="Tu próxima visita, con datos en la mano"
                text="Introduce la vivienda que te interesa y obtén un análisis completo en segundos. Gratis, sin compromiso."
                ctaText="Analizar una vivienda"
                rolNombre="inversor"
            />
        </LandingLayout>
    );
};

export default LandingComprador;
